import { FirebaseError } from 'firebase/app';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { getMessaging, getToken, Messaging, onMessage } from 'firebase/messaging';
import { db } from './firebaseCore';

  
  interface TokenData {
    email: string;
    token: string;
    lastUpdated: string;
  }
  
  interface MessageListener {
    subscribe: (callback: (payload: NotificationPayload) => void) => () => void;
    error: Error | null;
  }

interface NotificationPayload {
  notification?: {
    title?: string;
    body?: string;
  };
  data?: Record<string, string>;
}

interface TokenData {
  email: string;
  token: string;
  lastUpdated: string;
}

class MessagingService {
  private messaging: Messaging | null = null;
  private vapidKey = 'BD4TXNg_cC-HYB6DXJhjmcQ_VJXyCZg7ch3iwQXNyB3KJ5hEh-1JRPO4-aJ6G4Uk2w1KC-uL7Ken5Sa3Szdfmog';
  private initialized = false;

  constructor() {
    this.initializeMessaging();
  }

  private initializeMessaging(): void {
    if (typeof window !== 'undefined' && !this.initialized) {
      try {
        this.messaging = getMessaging();
        this.initialized = true;
      } catch (error) {
        console.error('Failed to initialize messaging:', error);
        this.messaging = null;
      }
    }
  }

  private async saveTokenToFirestore(userEmail: string, token: string): Promise<void> {
    if (!userEmail || !token) {
      throw new Error('Invalid parameters for token storage');
    }

    const tokenData: TokenData = {
      email: userEmail,
      token,
      lastUpdated: new Date().toISOString()
    };

    try {
      // Create a consistent document ID based on the user's email
      const docId = `token_${userEmail.replace(/[^a-zA-Z0-9]/g, '_')}`;
      const tokenRef = doc(db, 'user_tokens', docId);

      const existingDoc = await getDoc(tokenRef);

      if (existingDoc.exists()) {
        const existingData = existingDoc.data() as TokenData;
        if (existingData.token !== token) {
          await setDoc(tokenRef, tokenData, { merge: true });
        } else {
          // Just update the lastUpdated timestamp
          await setDoc(tokenRef, { lastUpdated: tokenData.lastUpdated }, { merge: true });
        }
      } else {
        await setDoc(tokenRef, tokenData);
      }
    } catch (error) {
      console.error('Error saving token:', error);
      if (error instanceof FirebaseError) {
        console.error('Firebase error code:', error.code);
        console.error('Firebase error message:', error.message);
      }
      throw error;
    }
}

  async requestPermission(userEmail: string): Promise<string> {
    if (!userEmail) {
      throw new Error('User email is required');
    }

    if (!this.messaging) {
      this.initializeMessaging();
      if (!this.messaging) {
        throw new Error('Messaging service not available');
      }
    }

    try {
      // Verify service worker support
      if (!('serviceWorker' in navigator)) {
        throw new Error('Service Worker not supported');
      }

      // Register service worker with retry logic
      const registration = await this.registerServiceWorkerWithRetry();
      
      // Request notification permission
      const permission = await this.requestNotificationPermission();
      if (permission !== 'granted') {
        throw new Error('Notification permission denied');
      }

      // Get FCM token
      const token = await this.getFCMToken(registration);
      if (!token) {
        throw new Error('Failed to get FCM token');
      }

      // Save token to Firestore
      // const docId = `token_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      await this.saveTokenToFirestore(userEmail, token);

      return token;
    } catch (error) {
      console.error('Permission request failed:', error);
      throw error;
    }
  }

  private async requestNotificationPermission(): Promise<NotificationPermission> {
    try {
      return await Notification.requestPermission();
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      throw new Error('Failed to request notification permission');
    }
  }

  private async registerServiceWorkerWithRetry(
    retries = 3,
    delay = 1000
  ): Promise<ServiceWorkerRegistration> {
    for (let i = 0; i < retries; i++) {
      try {
        let registration = await navigator.serviceWorker.getRegistration();
        
        if (!registration) {
          registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js', {
            scope: '/'
          });
        }
        
        return registration;
      } catch (error) {
        console.error(`Service worker registration attempt ${i + 1} failed:`, error);
        if (i === retries - 1) throw error;
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
    throw new Error('Failed to register service worker after multiple attempts');
  }

  private async getFCMToken(registration: ServiceWorkerRegistration): Promise<string> {
    if (!this.messaging) {
      throw new Error('Messaging not initialized');
    }

    try {
      await navigator.serviceWorker.ready;
      
      const token = await getToken(this.messaging, {
        vapidKey: this.vapidKey,
        serviceWorkerRegistration: registration
      });
      
      if (!token) {
        throw new Error('Empty token received');
      }
      
      return token;
    } catch (error) {
      console.error('Error getting FCM token:', error);
      throw error;
    }
  }

  onMessageListener(): MessageListener {
    if (!this.messaging) {
      return {
        subscribe: () => () => {},
        error: new Error('Messaging not initialized')
      };
    }

    return {
      subscribe: (callback: (payload: NotificationPayload) => void) => {
        try {
          return onMessage(this.messaging!, (payload) => {
            try {
              callback(payload);
            } catch (error) {
              console.error('Error in message callback:', error);
            }
          });
        } catch (error) {
          console.error('Error setting up message listener:', error);
          return () => {};
        }
      },
      error: null
    };
  }
}

export const messagingService = new MessagingService();